// Công tắc "Quản lý | Bán hàng" (Đại lý / CTV) + màn chọn lần đầu "Bạn muốn làm gì?".
// Dùng chung cho khung Gốc (thanh bên máy tính, thanh trên điện thoại) và Glass.

import { Network, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { WORK_MODE_LABEL, type WorkMode } from '@/lib/workMode'

const ORDER: WorkMode[] = ['manage', 'sell']

export function ModeSwitch({
  mode,
  onChange,
  className,
  size = 'md',
}: {
  mode: WorkMode
  onChange: (m: WorkMode) => void
  className?: string
  /** sm: thanh trên điện thoại */
  size?: 'sm' | 'md'
}) {
  // Nền màu là một "nút gạt" trượt giữa hai nấc (0,34 giây) — cùng nhịp với nội dung trượt theo hướng
  const sellOn = mode === 'sell'
  return (
    <div role="radiogroup" aria-label="Chế độ làm việc" className={cn('relative inline-grid grid-cols-2 rounded-lg border border-rule bg-paperSoft p-0.5', className)}>
      <span
        aria-hidden
        className="mode-thumb absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-accent shadow-sm"
        style={{ transform: sellOn ? 'translateX(100%)' : 'translateX(0)' }}
      />
      {ORDER.map((m) => {
        const active = m === mode
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => !active && onChange(m)}
            className={cn(
              'relative z-[1] whitespace-nowrap rounded-md font-semibold transition-colors duration-300',
              size === 'sm' ? 'min-h-9 px-3 text-xs' : 'min-h-9 px-3 text-sm',
              active ? 'text-onAccent' : 'text-inkSoft hover:text-ink',
            )}
          >
            {WORK_MODE_LABEL[m]}
          </button>
        )
      })}
    </div>
  )
}

const CHOICES: { mode: WorkMode; icon: typeof Network; title: string }[] = [
  { mode: 'manage', icon: Network, title: 'Quản lý' },
  { mode: 'sell', icon: ShoppingBag, title: 'Bán hàng' },
]

/** Hỏi một lần duy nhất (lần đầu đăng nhập Đại lý / CTV trên máy này). */
export function ModeChooser({
  open,
  isAgency,
  onPick,
  onDismiss,
}: {
  open: boolean
  isAgency: boolean
  onPick: (m: WorkMode) => void
  /** Đóng không chọn (Esc / bấm ra ngoài) — giữ chế độ đang ở, không hỏi lại. */
  onDismiss: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Bạn muốn làm gì?</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {CHOICES.map((c) => (
            <button
              key={c.mode}
              type="button"
              onClick={() => onPick(c.mode)}
              // Cùng màu với chế độ đó trên giao diện (Quản lý: cam Đại lý / tím CTV, Bán hàng: xanh)
              className={cn(
                c.mode === 'sell' ? 'tone-blue' : isAgency ? 'tone-orange' : 'tone-violet',
                'motion-state flex min-h-[120px] flex-col items-start gap-2 rounded-xl border border-rule bg-raise p-4 text-left hover:border-toneFg hover:bg-toneBg/40 cursor-pointer',
              )}
            >
              <span className="grid size-10 place-items-center rounded-lg bg-toneBg text-toneFg">
                <c.icon className="size-5" aria-hidden />
              </span>
              <span className="text-base font-semibold text-ink">{c.title}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
